import { useClient } from '@cortex/react-query';
import type { CortexClient } from '@cortex/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { InfoHint } from '../components/common/InfoHint';
import { PageHeader } from '../components/common/PageHeader';
import { buildS3SourceBody, generateExternalId, type S3AuthMode } from '../lib/s3-data-source';

// The Formant principal ARN the customer trusts in their role's trust policy.
// Prod-only: set via build env only in prod, so the assume-role handoff block
// simply doesn't render in dev/stage (which use static keys). See
// docs/s3-assume-role-design.md §6.
const formantS3PrincipalArn = import.meta.env.VITE_FORMANT_S3_PRINCIPAL_ARN as string | undefined;

export const Route = createFileRoute('/app-connections')({ component: AppConnectionsPage });

interface ISlackInstallation {
  connected: boolean;
  teamId?: string;
  teamName?: string;
}

interface IChannelMonitor {
  id: string;
  channelId: string;
  channelName: string;
  workflowId: string;
  isEnabled: boolean;
  filterMode: 'ALL' | 'REGEX';
  filterPattern?: string | null;
  messageFilterMode: 'ALL' | 'REGEX';
  messageFilterPattern?: string | null;
  formatterEnabled: boolean;
  formatterPrompt?: string | null;
  formatterModelConfigId?: string | null;
}

interface IWorkflow {
  id: string;
  name: string;
}

interface IModelConfig {
  id: string;
  name: string;
}

interface IS3Config {
  region?: string;
  keyPrefix?: string[];
  readOnly?: boolean;
  endpoint?: string;
  forcePathStyle?: boolean;
  authMode?: S3AuthMode;
  roleArn?: string;
}

// Redacted shape returned by v1/data-sources — `creds` is never sent back; the
// server exposes only `hasCreds` so the UI can show whether one is set.
interface IDataSource {
  id: string;
  name: string;
  type: 'SNOWFLAKE' | 'POSTGRES' | 'REST' | 'FORMANT' | 'S3' | 'OTHER';
  endpoint: string;
  config: IS3Config;
  hasCreds: boolean;
  createdAt: string;
  updatedAt: string;
}

const stack6: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' };
const stack4: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' };
const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const rowH: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

const tabBase: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  textDecoration: 'none',
  borderBottom: '2px solid transparent',
  marginBottom: '-1px',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  fontSize: 13,
  fontWeight: 500,
};

const tabActive: CSSProperties = {
  ...tabBase,
  color: 'var(--text-primary)',
  borderBottom: '2px solid var(--horizon-glow, var(--text-primary))',
};

function slackInstallationKey() {
  return ['slack-installation'] as const;
}
function channelMonitorsKey() {
  return ['slack-channel-monitors'] as const;
}
function workflowsKey() {
  return ['workflows-list'] as const;
}
function dataSourcesKey() {
  return ['data-sources'] as const;
}

function openOAuthPopup(url: string, onComplete: () => void): void {
  const w = 600;
  const h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    url,
    'cortex-slack-oauth',
    `width=${w},height=${h},left=${left},top=${top}`,
  );
  if (!popup) {
    alert('Popup blocked — allow popups for this site.');
    return;
  }
  let finished = false;
  function scheduleRefreshBurst() {
    let remaining = 15;
    onComplete();
    const timer = window.setInterval(() => {
      onComplete();
      remaining -= 1;
      if (remaining <= 0) window.clearInterval(timer);
    }, 1000);
  }
  function dispose() {
    window.removeEventListener('message', onMessage);
    window.clearInterval(closedPoller);
    window.clearTimeout(disposeTimer);
  }
  function finish() {
    if (finished) return;
    finished = true;
    dispose();
    scheduleRefreshBurst();
  }
  function onMessage(e: MessageEvent) {
    if (
      e.data &&
      typeof e.data === 'object' &&
      (e.data as { source?: string }).source === 'cortex-oauth'
    )
      finish();
  }
  window.addEventListener('message', onMessage);
  const closedPoller = window.setInterval(() => {
    if (popup.closed) finish();
  }, 300);
  const disposeTimer = window.setTimeout(() => dispose(), 15 * 60 * 1000);
}

function AppConnectionsPage() {
  const client = useClient();
  const [tab, setTab] = useState<'connection' | 'monitors'>('connection');

  const status = useQuery({
    queryKey: slackInstallationKey(),
    queryFn: () => client.http.get('v1/integrations/slack/installation').json<ISlackInstallation>(),
  });

  const isConnected = status.data?.connected === true;

  return (
    <div style={stack6}>
      <PageHeader title="App Connections" />
      <div className="card">
        <div className="card-header">
          <span className="t-mono-label">SLACK</span>
          <h3 className="t-h3">Slack</h3>
        </div>

        <nav
          role="tablist"
          style={{
            display: 'flex',
            gap: 'var(--sp-1)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '0 var(--sp-4)',
          }}
        >
          <button
            role="tab"
            aria-selected={tab === 'connection'}
            style={tab === 'connection' ? tabActive : tabBase}
            onClick={() => setTab('connection')}
          >
            Connection
          </button>
          <button
            role="tab"
            aria-selected={tab === 'monitors'}
            style={tab === 'monitors' ? tabActive : tabBase}
            onClick={() => setTab('monitors')}
            disabled={!isConnected}
          >
            Channel Monitors
          </button>
        </nav>

        <div className="card-body">
          {tab === 'connection' ? (
            <ConnectionTab client={client} status={status} />
          ) : (
            <MonitorsTab client={client} />
          )}
        </div>
      </div>

      <S3ConnectionsCard client={client} />
    </div>
  );
}

function S3ConnectionsCard({ client }: { client: CortexClient }) {
  const qc = useQueryClient();
  // Swallow the invalidate rejection: it's passed bare to `onSuccess` (which
  // TanStack does not await), so an unhandled rejection here would surface as a
  // console error. A failed re-fetch is non-fatal — the query's own error state
  // renders below and the next user action re-queries.
  const refetch = () => qc.invalidateQueries({ queryKey: dataSourcesKey() }).catch(() => undefined);

  const sources = useQuery({
    queryKey: dataSourcesKey(),
    queryFn: () => client.http.get('v1/data-sources?limit=200').json<{ items: IDataSource[] }>(),
  });

  const s3Sources = (sources.data?.items ?? []).filter((d) => d.type === 'S3');

  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/data-sources/${id}`),
    onSuccess: refetch,
  });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="card">
      <div className="card-header">
        <span className="t-mono-label">AMAZON S3</span>
        <h3 className="t-h3">S3 Buckets</h3>
      </div>

      <div className="card-body">
        <div style={stack4}>
          <div style={{ ...rowH, justifyContent: 'space-between' }}>
            <p className="t-sm t-secondary">
              Connect an S3 bucket so AI agents can list and read its objects. Credentials are
              encrypted at rest and never returned by the API.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              style={{ flexShrink: 0 }}
              onClick={() => {
                // If an inline edit is open, close it and open the add form
                // (don't blind-toggle showForm, which is already false here).
                if (editingId !== null) {
                  setEditingId(null);
                  setShowForm(true);
                } else {
                  setShowForm((v) => !v);
                }
              }}
            >
              {showForm ? 'Cancel' : '+ Add bucket'}
            </button>
          </div>

          {showForm ? (
            <S3SourceForm
              client={client}
              onDone={async () => {
                await refetch();
                setShowForm(false);
              }}
            />
          ) : null}

          {sources.isLoading ? (
            <p className="t-sm t-muted">Loading…</p>
          ) : sources.error ? (
            <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
              Failed to load: {(sources.error as Error).message}
            </p>
          ) : !s3Sources.length ? (
            <p className="t-sm t-muted">No S3 buckets connected yet. Add one above.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {s3Sources.map((d, i) => (
                <li
                  key={d.id}
                  style={{
                    padding: 'var(--sp-3) 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ ...rowH, justifyContent: 'space-between' }}>
                    <div style={stack3}>
                      <div style={rowH}>
                        <span style={{ fontWeight: 500 }}>{d.name}</span>
                        <span className="badge badge--neutral t-mono">{d.endpoint}</span>
                        {d.config?.region ? (
                          <span className="badge badge--neutral">{d.config.region}</span>
                        ) : null}
                        <span className="badge badge--neutral">
                          {d.config?.readOnly === false ? 'read/write' : 'read-only'}
                        </span>
                        <span className="badge badge--neutral">
                          {d.config?.authMode === 'assumeRole' ? 'assume-role' : 'static keys'}
                        </span>
                        {d.hasCreds ? null : (
                          <span className="badge" style={{ color: 'var(--status-danger)' }}>
                            no credentials
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={rowH}>
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        onClick={() => {
                          setEditingId((prev) => (prev === d.id ? null : d.id));
                          setShowForm(false);
                        }}
                      >
                        {editingId === d.id ? 'Cancel' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--outline btn--sm"
                        style={{ color: 'var(--status-danger)' }}
                        disabled={remove.isPending && remove.variables === d.id}
                        onClick={() => {
                          if (remove.isPending) return;
                          if (confirm(`Delete S3 connection "${d.name}"?`)) remove.mutate(d.id);
                        }}
                      >
                        {remove.isPending && remove.variables === d.id ? 'Deleting…' : 'Delete'}
                      </button>
                      {remove.error && remove.variables === d.id ? (
                        <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                          {(remove.error as Error).message}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {editingId === d.id ? (
                    <S3SourceForm
                      client={client}
                      source={d}
                      onDone={async () => {
                        await refetch();
                        setEditingId(null);
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function S3SourceForm({
  client,
  source,
  onDone,
}: {
  client: CortexClient;
  source?: IDataSource;
  onDone: () => void;
}) {
  const isEdit = source !== undefined;

  const [name, setName] = useState(source?.name ?? '');
  const [bucket, setBucket] = useState(source?.endpoint ?? '');
  const [region, setRegion] = useState(source?.config?.region ?? '');
  const [keyPrefix, setKeyPrefix] = useState((source?.config?.keyPrefix ?? []).join(', '));
  const [readOnly, setReadOnly] = useState(source?.config?.readOnly ?? true);
  const [forcePathStyle, setForcePathStyle] = useState(source?.config?.forcePathStyle ?? false);
  const [s3Endpoint, setS3Endpoint] = useState(source?.config?.endpoint ?? '');
  const originalAuthMode: S3AuthMode = source?.config?.authMode ?? 'staticKeys';
  const [authMode, setAuthMode] = useState<S3AuthMode>(originalAuthMode);
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  // roleArn is non-secret and returned by the API, so it prefills on edit.
  const [roleArn, setRoleArn] = useState(source?.config?.roleArn ?? '');
  // externalId is a secret: never returned, so it starts blank (blank = keep
  // existing on edit). On create the operator generates one to send the customer.
  const [externalId, setExternalId] = useState('');

  // Secrets are required on create, and on edit only when the auth mode was
  // switched (the stored creds no longer match the selected mode). Otherwise a
  // blank secret means "keep existing".
  const modeChanged = isEdit && originalAuthMode !== authMode;
  const secretRequired = !isEdit || modeChanged;

  const mutation = useMutation({
    mutationFn: () => {
      const body = buildS3SourceBody({
        name,
        bucket,
        region,
        keyPrefix,
        readOnly,
        forcePathStyle,
        endpoint: s3Endpoint,
        authMode,
        accessKeyId,
        secretAccessKey,
        roleArn,
        externalId,
      });
      return isEdit
        ? client.http.patch(`v1/data-sources/${source.id}`, { json: body }).json<IDataSource>()
        : client.http.post('v1/data-sources', { json: body }).json<IDataSource>();
    },
    onSuccess: onDone,
  });

  return (
    <form
      style={{
        ...stack3,
        ...(isEdit ? { marginTop: 'var(--sp-3)' } : {}),
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius)',
        padding: 'var(--sp-4)',
      }}
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <p className="t-mono-label" style={{ color: 'var(--text-secondary)' }}>
        {isEdit ? 'EDIT S3 CONNECTION' : 'NEW S3 CONNECTION'}
      </p>
      <div style={grid2}>
        <label className="field">
          <span className="field-label">Name</span>
          <input
            required={true}
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="burro-prod-s3"
          />
        </label>
        <label className="field">
          <span className="field-label">Bucket name</span>
          <input
            required={true}
            className="input"
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            placeholder="my-bucket"
          />
        </label>
      </div>
      <div style={grid2}>
        <label className="field">
          <span className="field-label">Region</span>
          <input
            required={true}
            className="input"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="us-east-1"
          />
        </label>
        <label className="field">
          <span className="field-label">
            Key prefix allow-list
            <InfoHint
              text="Optional comma-separated list of key prefixes. When set, agents may only read/write objects whose key starts with one of these — bounds access to a dedicated area of the bucket. Leave blank for the whole bucket."
              side="right"
            />
          </span>
          <input
            className="input"
            value={keyPrefix}
            onChange={(e) => setKeyPrefix(e.target.value)}
            placeholder="docs/, exports/"
          />
        </label>
      </div>
      <label
        className="field"
        style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--sp-2)' }}
      >
        <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />
        <span className="field-label" style={{ marginBottom: 0 }}>
          Read-only
          <InfoHint
            text="When enabled, the agent write tool is refused for this connection. Leave on unless an agent genuinely needs to write objects."
            side="right"
          />
        </span>
      </label>

      <details>
        <summary className="t-sm t-secondary" style={{ cursor: 'pointer' }}>
          Advanced (S3-compatible servers)
        </summary>
        <div style={{ ...stack3, marginTop: 'var(--sp-3)' }}>
          <label className="field">
            <span className="field-label">
              Custom endpoint
              <InfoHint
                text="Leave blank for AWS S3 (the SDK uses the regional default). Set only for S3-compatible servers like MinIO or LocalStack, e.g. http://localhost:9000."
                side="right"
              />
            </span>
            <input
              className="input"
              value={s3Endpoint}
              onChange={(e) => setS3Endpoint(e.target.value)}
              placeholder="https://s3-compatible.example.com"
            />
          </label>
          <label
            className="field"
            style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--sp-2)' }}
          >
            <input
              type="checkbox"
              checked={forcePathStyle}
              onChange={(e) => setForcePathStyle(e.target.checked)}
            />
            <span className="field-label" style={{ marginBottom: 0 }}>
              Force path-style addressing
              <InfoHint
                text="Required by most non-AWS servers (incl. MinIO). Leave off for real AWS S3, which uses virtual-hosted addressing."
                side="right"
              />
            </span>
          </label>
        </div>
      </details>

      <label className="field">
        <span className="field-label">
          Authentication
          <InfoHint
            text="Static keys: a long-lived IAM access-key pair. Assume role (recommended): the customer creates a role in their account that Formant assumes via STS for short-lived credentials — no long-lived secret to store."
            side="right"
          />
        </span>
        <select
          className="select-native"
          value={authMode}
          onChange={(e) => setAuthMode(e.target.value as S3AuthMode)}
        >
          <option value="staticKeys">Static access keys</option>
          <option value="assumeRole">Assume role (STS + External ID)</option>
        </select>
      </label>

      {authMode === 'staticKeys' ? (
        <>
          <div style={grid2}>
            <label className="field">
              <span className="field-label">Access key ID</span>
              <input
                required={secretRequired}
                className="input"
                autoComplete="off"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                placeholder={secretRequired ? 'AKIA…' : 'Leave blank to keep existing'}
              />
            </label>
            <label className="field">
              <span className="field-label">Secret access key</span>
              <input
                required={secretRequired}
                type="password"
                className="input"
                autoComplete="off"
                value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                placeholder={secretRequired ? '••••••••' : 'Leave blank to keep existing'}
              />
            </label>
          </div>
          {isEdit && !modeChanged ? (
            <p className="t-xs t-muted">
              Credentials are never returned by the API. Leave both blank to keep the existing
              credentials, or fill both in to replace them.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <label className="field">
            <span className="field-label">
              Role ARN
              <InfoHint
                text="The IAM role the customer creates in their AWS account for Formant to assume. e.g. arn:aws:iam::123456789012:role/formant-s3-read"
                side="right"
              />
            </span>
            <input
              required={true}
              className="input"
              autoComplete="off"
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              placeholder="arn:aws:iam::123456789012:role/formant-s3-read"
            />
          </label>
          <label className="field">
            <span className="field-label">
              External ID
              <InfoHint
                text="A per-customer secret embedded in the role's trust condition (prevents the confused-deputy problem). Generate one and send it to the customer to include in their trust policy."
                side="right"
              />
            </span>
            <div style={{ ...rowH, alignItems: 'stretch' }}>
              <input
                required={secretRequired}
                className="input"
                autoComplete="off"
                style={{ flex: 1 }}
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                placeholder={secretRequired ? 'formant-…' : 'Leave blank to keep existing'}
              />
              <button
                type="button"
                className="btn btn--outline btn--sm"
                style={{ flexShrink: 0 }}
                onClick={() => setExternalId(generateExternalId(name || bucket))}
              >
                Generate
              </button>
            </div>
          </label>

          {formantS3PrincipalArn ? (
            <div
              style={{
                ...stack3,
                background: 'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius)',
                padding: 'var(--sp-3)',
              }}
            >
              <p className="t-xs t-secondary">
                Send these to the customer to build their role&rsquo;s trust policy:
              </p>
              <div style={stack3}>
                <div style={stack3}>
                  <span className="t-mono-label">FORMANT PRINCIPAL ARN</span>
                  <code className="t-mono t-xs" style={{ wordBreak: 'break-all' }}>
                    {formantS3PrincipalArn}
                  </code>
                </div>
                {externalId ? (
                  <div style={stack3}>
                    <span className="t-mono-label">EXTERNAL ID</span>
                    <code className="t-mono t-xs" style={{ wordBreak: 'break-all' }}>
                      {externalId}
                    </code>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {isEdit && !modeChanged ? (
            <p className="t-xs t-muted">
              The External ID is never returned by the API. Leave it blank to keep the existing one,
              or generate a new one to rotate it (the customer must update their trust policy).
            </p>
          ) : null}
        </>
      )}

      <div style={rowH}>
        <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save connection'}
        </button>
        {mutation.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(mutation.error as Error).message}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function ConnectionTab({
  client,
  status,
}: {
  client: CortexClient;
  status: ReturnType<typeof useQuery<ISlackInstallation>>;
}) {
  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: slackInstallationKey() });

  const connect = useMutation({
    mutationFn: () => client.http.get('v1/integrations/slack/install').json<{ url: string }>(),
    onSuccess: ({ url }) => openOAuthPopup(url, refetch),
  });

  const disconnect = useMutation({
    mutationFn: () => client.http.delete('v1/integrations/slack/installation'),
    onSuccess: refetch,
  });

  const installation = status.data;

  return (
    <div style={stack3}>
      <p className="t-sm t-secondary">
        Receive messages from Slack channels and automatically trigger workflows. Once connected,
        use the Channel Monitors tab to link channels to specific workflows.
      </p>

      {status.isLoading ? (
        <p className="t-sm t-muted">Loading…</p>
      ) : status.error ? (
        <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
          Failed to load: {(status.error as Error).message}
        </p>
      ) : installation?.connected ? (
        <div style={stack3}>
          <div style={rowH}>
            <span
              className="badge"
              style={{ background: 'var(--status-success-subtle)', color: 'var(--status-success)' }}
            >
              Connected
            </span>
            <span className="t-sm t-secondary">{installation.teamName}</span>
          </div>
          <div style={rowH}>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              disabled={disconnect.isPending}
              onClick={() => {
                if (confirm('Disconnect Slack? Channel monitors will stop triggering workflows.'))
                  disconnect.mutate();
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </button>
            {disconnect.error ? (
              <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {(disconnect.error as Error).message}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={rowH}>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
          >
            {connect.isPending ? 'Opening…' : 'Connect to Slack'}
          </button>
          {connect.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {(connect.error as Error).message}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function MonitorsTab({ client }: { client: CortexClient }) {
  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: channelMonitorsKey() });

  const monitors = useQuery({
    queryKey: channelMonitorsKey(),
    queryFn: () =>
      client.http
        .get('v1/integrations/slack/channel-monitors')
        .json<{ items: IChannelMonitor[] }>(),
  });

  const workflows = useQuery({
    queryKey: workflowsKey(),
    queryFn: () => client.http.get('v1/workflows?limit=200').json<{ items: IWorkflow[] }>(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/integrations/slack/channel-monitors/${id}`),
    onSuccess: refetch,
  });

  const toggle = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      client.http.patch(`v1/integrations/slack/channel-monitors/${id}`, { json: { isEnabled } }),
    onSuccess: refetch,
  });

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const workflowMap = new Map(workflows.data?.items.map((w) => [w.id, w.name]) ?? []);

  return (
    <div style={stack4}>
      <div style={{ ...rowH, justifyContent: 'space-between' }}>
        <p className="t-sm t-secondary">
          Each monitor links a Slack channel to a workflow. When a message is posted in the channel,
          the workflow is triggered automatically.
        </p>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ flexShrink: 0 }}
          onClick={() => {
            setShowForm((v) => !v);
            setEditingId(null);
          }}
        >
          {showForm ? 'Cancel' : '+ Add monitor'}
        </button>
      </div>

      {showForm ? (
        <MonitorForm
          client={client}
          workflows={workflows.data?.items ?? []}
          onDone={async () => {
            await refetch();
            setShowForm(false);
          }}
        />
      ) : null}

      {monitors.isLoading ? (
        <p className="t-sm t-muted">Loading…</p>
      ) : monitors.error ? (
        <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
          Failed to load: {(monitors.error as Error).message}
        </p>
      ) : !monitors.data?.items.length ? (
        <p className="t-sm t-muted">No channel monitors yet. Add one above.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {monitors.data.items.map((m, i) => (
            <li
              key={m.id}
              style={{
                padding: 'var(--sp-3) 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
              }}
            >
              <div style={{ ...rowH, justifyContent: 'space-between' }}>
                <div style={stack3}>
                  <div style={rowH}>
                    <span style={{ fontWeight: 500 }}>#{m.channelName}</span>
                    <span className="badge badge--neutral">
                      {workflowMap.get(m.workflowId) ?? m.workflowId}
                    </span>
                    {m.filterMode === 'REGEX' && m.filterPattern ? (
                      <span
                        className="badge badge--neutral t-mono"
                        title={`Filter: ${m.filterPattern}`}
                      >
                        regex
                      </span>
                    ) : null}
                  </div>
                  <span className="t-xs t-muted">{m.channelId}</span>
                </div>
                <div style={rowH}>
                  <label style={{ ...rowH, gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={m.isEnabled}
                      onChange={() => toggle.mutate({ id: m.id, isEnabled: !m.isEnabled })}
                    />
                    {m.isEnabled ? 'Enabled' : 'Disabled'}
                  </label>
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    onClick={() => {
                      setEditingId((prev) => (prev === m.id ? null : m.id));
                      setShowForm(false);
                    }}
                  >
                    {editingId === m.id ? 'Cancel' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    style={{ color: 'var(--status-danger)' }}
                    onClick={() => {
                      if (confirm('Delete this channel monitor?')) remove.mutate(m.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {editingId === m.id ? (
                <MonitorForm
                  client={client}
                  monitor={m}
                  workflows={workflows.data?.items ?? []}
                  onDone={async () => {
                    await refetch();
                    setEditingId(null);
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MonitorForm({
  client,
  workflows,
  monitor,
  onDone,
}: {
  client: CortexClient;
  workflows: IWorkflow[];
  monitor?: IChannelMonitor;
  onDone: () => void;
}) {
  const isEdit = monitor !== undefined;

  const [channelId, setChannelId] = useState(monitor?.channelId ?? '');
  const [channelName, setChannelName] = useState(monitor?.channelName ?? '');
  const [workflowId, setWorkflowId] = useState(monitor?.workflowId ?? '');
  const [filterMode, setFilterMode] = useState<'ALL' | 'REGEX'>(monitor?.filterMode ?? 'ALL');
  const [filterPattern, setFilterPattern] = useState(monitor?.filterPattern ?? '');
  const [messageFilterMode, setMessageFilterMode] = useState<'ALL' | 'REGEX'>(
    monitor?.messageFilterMode ?? 'ALL',
  );
  const [messageFilterPattern, setMessageFilterPattern] = useState(
    monitor?.messageFilterPattern ?? '',
  );
  const [formatterEnabled, setFormatterEnabled] = useState(monitor?.formatterEnabled ?? false);
  const [formatterModelConfigId, setFormatterModelConfigId] = useState(
    monitor?.formatterModelConfigId ?? '',
  );
  const [formatterPrompt, setFormatterPrompt] = useState(monitor?.formatterPrompt ?? '');

  const modelConfigs = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs').json<{ items: IModelConfig[] }>(),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        channelId: channelId.trim(),
        channelName: channelName.trim(),
        workflowId,
        filterMode,
        filterPattern: filterMode === 'REGEX' ? filterPattern.trim() : undefined,
        messageFilterMode,
        messageFilterPattern:
          messageFilterMode === 'REGEX' ? messageFilterPattern.trim() : undefined,
        formatterEnabled,
        formatterModelConfigId: formatterEnabled ? formatterModelConfigId || null : null,
        formatterPrompt: formatterEnabled ? formatterPrompt.trim() || null : null,
      };
      return isEdit
        ? client.http
            .patch(`v1/integrations/slack/channel-monitors/${monitor.id}`, { json: body })
            .json<IChannelMonitor>()
        : client.http
            .post('v1/integrations/slack/channel-monitors', { json: body })
            .json<IChannelMonitor>();
    },
    onSuccess: onDone,
  });

  return (
    <form
      style={{
        ...stack3,
        ...(isEdit ? { marginTop: 'var(--sp-3)' } : {}),
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius)',
        padding: 'var(--sp-4)',
      }}
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <p className="t-mono-label" style={{ color: 'var(--text-secondary)' }}>
        {isEdit ? 'EDIT CHANNEL MONITOR' : 'NEW CHANNEL MONITOR'}
      </p>
      <div style={grid2}>
        <label className="field">
          <span className="field-label">Channel ID</span>
          <input
            required={true}
            className="input"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            placeholder="C01234ABC"
          />
        </label>
        <label className="field">
          <span className="field-label">Channel name</span>
          <input
            required={true}
            className="input"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            placeholder="general"
          />
        </label>
      </div>
      <label className="field">
        <span className="field-label">Workflow</span>
        <select
          required={true}
          className="select-native"
          value={workflowId}
          onChange={(e) => setWorkflowId(e.target.value)}
        >
          <option value="">Select a workflow…</option>
          {workflows.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">
          Sender filter
          <InfoHint text="Regex tested against the sender's Slack username. Independent of the content filter below — if both are set to regex, a message must match both to trigger the workflow." />
        </span>
        <select
          className="select-native"
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value as 'ALL' | 'REGEX')}
        >
          <option value="ALL">All senders</option>
          <option value="REGEX">Regex filter on username</option>
        </select>
      </label>
      {filterMode === 'REGEX' ? (
        <label className="field">
          <span className="field-label">Sender pattern (regex)</span>
          <input
            required={true}
            className="input"
            value={filterPattern}
            onChange={(e) => setFilterPattern(e.target.value)}
            placeholder="^jane"
          />
        </label>
      ) : null}
      <label className="field">
        <span className="field-label">
          Content filter
          <InfoHint text="Regex tested against the message text (dotAll — '.' matches newlines, so patterns can span multiple lines). Use this to only trigger on structured messages, e.g. incident reports, while ignoring free-form chat in the same channel." />
        </span>
        <select
          className="select-native"
          value={messageFilterMode}
          onChange={(e) => setMessageFilterMode(e.target.value as 'ALL' | 'REGEX')}
        >
          <option value="ALL">All messages</option>
          <option value="REGEX">Regex filter on message text</option>
        </select>
      </label>
      {messageFilterMode === 'REGEX' ? (
        <label className="field">
          <span className="field-label">Content pattern (regex)</span>
          <input
            required={true}
            className="input"
            value={messageFilterPattern}
            onChange={(e) => setMessageFilterPattern(e.target.value)}
            placeholder="^Issue:.*Jira ticket Link"
          />
        </label>
      ) : null}
      <label
        className="field"
        style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--sp-2)' }}
      >
        <input
          type="checkbox"
          checked={formatterEnabled}
          onChange={(e) => setFormatterEnabled(e.target.checked)}
        />
        <span className="field-label" style={{ marginBottom: 0 }}>
          Enable AI formatter
          <InfoHint
            text="When enabled, the workflow output is passed through an AI model before being posted as a reply. The model rewrites the raw output into a clean, readable message. You can optionally choose which model to use and provide custom formatting instructions."
            side="right"
          />
        </span>
      </label>
      {formatterEnabled ? (
        <>
          <label className="field">
            <span className="field-label">Formatter model</span>
            <select
              required={true}
              className="select-native"
              value={formatterModelConfigId}
              onChange={(e) => setFormatterModelConfigId(e.target.value)}
            >
              <option value="" disabled={true}>
                Select a model…
              </option>
              {(modelConfigs.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Formatting instructions (optional)</span>
            <textarea
              className="textarea"
              rows={6}
              value={formatterPrompt}
              onChange={(e) => setFormatterPrompt(e.target.value)}
              placeholder="Leave blank to use the default formatting prompt."
            />
          </label>
        </>
      ) : null}
      <div style={rowH}>
        <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save monitor'}
        </button>
        {mutation.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(mutation.error as Error).message}
          </span>
        ) : null}
      </div>
    </form>
  );
}
